import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getUserLists, removeFromListAPI } from "../api";
import { LIST_ITEMS } from "../utils/lists";

function ListMovieCard({ movie, onRemove }) {
  const navigate = useNavigate();
  const initials = movie.title
    ? movie.title.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <div
      onClick={() => navigate(`/movie/${movie.id}`)}
      style={{
        background: "#0f0f12", border: "0.5px solid #1e1e22",
        borderRadius: 14, overflow: "hidden", cursor: "pointer",
        transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
        position: "relative",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "rgba(255,92,53,0.45)";
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 8px 28px rgba(255,92,53,0.1)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "#1e1e22";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        height: 130, background: "#161618", position: "relative",
        overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {movie.poster_url ? (
          <img
            src={movie.poster_url} alt={movie.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 30, fontWeight: 800, color: "#2a2a32",
          }}>
            {initials}
          </span>
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, transparent 50%, rgba(9,9,11,0.55) 100%)",
          pointerEvents: "none",
        }} />
        <button
          onClick={e => { e.stopPropagation(); onRemove(movie.id); }}
          style={{
            position: "absolute", top: 6, right: 6,
            background: "rgba(8,8,10,0.82)", border: "0.5px solid #3a3a44",
            borderRadius: "50%", width: 24, height: 24,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#9898a6", fontSize: 14, cursor: "pointer",
            transition: "background 0.15s, color 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(180,30,30,0.85)"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(8,8,10,0.82)"; e.currentTarget.style.color = "#9898a6"; }}
        >×</button>
      </div>
      <div style={{ padding: "11px 13px 13px" }}>
        <div style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontWeight: 700, fontSize: 12, color: "#e8e8ee",
          lineHeight: 1.3, marginBottom: 3,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{movie.title}</div>
        <div style={{
          fontSize: 10, color: "#45454e", marginBottom: 6,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>{movie.year}</div>
        {movie.score && (
          <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
            <span style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: 19, fontWeight: 800,
              background: "linear-gradient(135deg, #FF5C35, #ff8a68)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>{movie.score}</span>
            <span style={{ fontSize: 10, color: "#45454e" }}>/10</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Lists() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeList, setActiveList] = useState("watchlist");
  const [lists,      setLists]      = useState({ watchlist: [], seen_it: [], favourites: [], hidden_gems: [] });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const fetchLists = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getUserLists(token);
      if (data && typeof data === "object" && !data.error && !data.msg) {
        setLists({
          watchlist:   data.watchlist   || [],
          seen_it:     data.seen_it     || [],
          favourites:  data.favourites  || [],
          hidden_gems: data.hidden_gems || [],
        });
      } else {
        setError(data?.error || data?.msg || "Failed to load lists");
      }
    } catch {
      setError("Could not connect to the server");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  async function handleRemove(movieId) {
    // Optimistic update
    setLists(prev => ({
      ...prev,
      [activeList]: prev[activeList].filter(m => m.id !== movieId),
    }));
    try {
      await removeFromListAPI(movieId, activeList, token);
    } catch {
      // Revert on failure
      fetchLists();
    }
  }

  const currentMovies = lists[activeList] || [];

  return (
    <div style={{ minHeight: "100vh", background: "#0b0b0c" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 20px" }}>

        <div style={{ marginBottom: 28 }}>
          <h1 style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 28, fontWeight: 800, color: "#f2f2f4",
            letterSpacing: "-0.6px", marginBottom: 6,
          }}>My Lists</h1>
          <p style={{ fontSize: 13, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Save movies to watch, revisit favourites, and uncover hidden gems.
          </p>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: "0.5px solid #1a1a1e", overflowX: "auto" }}>
          {LIST_ITEMS.map(list => {
            const count    = (lists[list.id] || []).length;
            const isActive = activeList === list.id;
            return (
              <button
                key={list.id}
                onClick={() => setActiveList(list.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "10px 20px", fontSize: 13, cursor: "pointer",
                  background: "transparent", border: "none",
                  borderBottom: isActive ? "2px solid #FF5C35" : "2px solid transparent",
                  color: isActive ? "#FF5C35" : "#55555e",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: "nowrap", transition: "color 0.15s",
                  marginBottom: "-1px",
                }}
              >
                <span>{list.icon}</span>
                <span>{list.label}</span>
                {count > 0 && (
                  <span style={{
                    background: isActive ? "rgba(255,92,53,0.15)" : "#1c1c1f",
                    color: isActive ? "#FF5C35" : "#55555e",
                    fontSize: 10, fontWeight: 700,
                    borderRadius: 10, padding: "1px 7px",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    border: isActive ? "0.5px solid rgba(255,92,53,0.25)" : "0.5px solid #252528",
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#3a3a44", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            loading your lists…
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 13, color: "#9a4a4a", marginBottom: 16, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{error}</div>
            <button onClick={fetchLists} style={{ background: "#FF5C35", color: "#fff", border: "none", padding: "10px 24px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              retry
            </button>
          </div>
        ) : currentMovies.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>
              {LIST_ITEMS.find(l => l.id === activeList)?.icon}
            </div>
            <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 18, fontWeight: 700, color: "#3a3a44", marginBottom: 8 }}>
              Nothing here yet
            </div>
            <div style={{ fontSize: 13, color: "#2a2a32", marginBottom: 28, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Start adding movies from any film page
            </div>
            <button onClick={() => navigate("/")} style={{ background: "#FF5C35", color: "#fff", border: "none", padding: "11px 28px", borderRadius: 22, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif", boxShadow: "0 4px 18px rgba(255,92,53,0.3)" }}>
              browse movies
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 14 }}>
            {currentMovies.map(m => (
              <ListMovieCard key={m.id} movie={m} onRemove={handleRemove} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
